import { useState } from 'react'
import { router } from '@inertiajs/react'
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isToday,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  addWeeks,
  subWeeks,
  startOfDay,
  endOfDay,
  addDays,
  subDays,
} from 'date-fns'
import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  UserIcon,
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { cn } from '@/lib/utils'

interface Appointment {
  id: number
  patient: {
    id: number
    display_name: string
    phone_number: string
  }
  appointment_type: string
  type_label: string
  scheduled_at: string
  formatted_time: string
  duration_minutes: number
  status: string
  status_label: string
}

interface AppointmentsCalendarViewProps {
  appointments: Appointment[]
  currentDate?: Date
  onDateChange?: (date: Date) => void
}

type CalendarView = 'month' | 'week' | 'day'

export function AppointmentsCalendarView({
  appointments,
  currentDate: initialDate,
  onDateChange,
}: AppointmentsCalendarViewProps) {
  const [currentDate, setCurrentDate] = useState(initialDate || new Date())
  const [view, setView] = useState<CalendarView>('month')

  const handleDateChange = (date: Date) => {
    setCurrentDate(date)
    onDateChange?.(date)
  }

  const handlePrevious = () => {
    if (view === 'month') {
      handleDateChange(subMonths(currentDate, 1))
    } else if (view === 'week') {
      handleDateChange(subWeeks(currentDate, 1))
    } else {
      handleDateChange(subDays(currentDate, 1))
    }
  }

  const handleNext = () => {
    if (view === 'month') {
      handleDateChange(addMonths(currentDate, 1))
    } else if (view === 'week') {
      handleDateChange(addWeeks(currentDate, 1))
    } else {
      handleDateChange(addDays(currentDate, 1))
    }
  }

  const handleToday = () => {
    handleDateChange(new Date())
  }

  const getTypeBadgeColor = (type: string): string => {
    switch (type) {
      case 'initial_consultation':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
      case 'follow_up_visit':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
      case 'lab_review':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
      case 'check_in':
        return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300'
      case 'phone_call':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
      case 'telehealth':
        return 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
    }
  }

  const getAppointmentsForDate = (date: Date): Appointment[] => {
    return appointments.filter(apt =>
      isSameDay(new Date(apt.scheduled_at), date)
    ).sort((a, b) =>
      new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime()
    )
  }

  const renderMonthView = () => {
    const monthStart = startOfMonth(currentDate)
    const monthEnd = endOfMonth(currentDate)
    const calendarStart = startOfWeek(monthStart)
    const calendarEnd = endOfWeek(monthEnd)
    const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd })

    return (
      <div className="grid grid-cols-7 gap-2">
        {/* Weekday headers */}
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div
            key={day}
            className="text-center text-sm font-semibold text-muted-foreground py-2"
          >
            {day}
          </div>
        ))}

        {/* Calendar days */}
        {days.map(day => {
          const dayAppointments = getAppointmentsForDate(day)
          const isCurrentMonth = isSameMonth(day, currentDate)
          const isDayToday = isToday(day)

          return (
            <div
              key={day.toISOString()}
              className={cn(
                'min-h-[120px] border rounded-lg p-2',
                isCurrentMonth ? 'bg-card' : 'bg-muted/30',
                isDayToday && 'ring-2 ring-primary'
              )}
            >
              <div className="flex items-center justify-between mb-2">
                <span
                  className={cn(
                    'text-sm font-medium',
                    !isCurrentMonth && 'text-muted-foreground',
                    isDayToday && 'bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center'
                  )}
                >
                  {format(day, 'd')}
                </span>
                {dayAppointments.length > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {dayAppointments.length}
                  </Badge>
                )}
              </div>

              <div className="space-y-1">
                {dayAppointments.slice(0, 3).map(apt => (
                  <div
                    key={apt.id}
                    onClick={() => router.visit(`/admin/appointments/${apt.id}`)}
                    className={cn(
                      'text-xs p-1 rounded cursor-pointer hover:opacity-80 transition-opacity truncate',
                      getTypeBadgeColor(apt.appointment_type)
                    )}
                  >
                    <div className="flex items-center gap-1">
                      <ClockIcon className="h-3 w-3" />
                      <span className="font-medium">{apt.formatted_time}</span>
                    </div>
                    <div className="truncate">{apt.patient.display_name}</div>
                  </div>
                ))}
                {dayAppointments.length > 3 && (
                  <div className="text-xs text-muted-foreground text-center">
                    +{dayAppointments.length - 3} more
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  const renderWeekView = () => {
    const weekStart = startOfWeek(currentDate)
    const weekEnd = endOfWeek(currentDate)
    const days = eachDayOfInterval({ start: weekStart, end: weekEnd })

    return (
      <div className="grid grid-cols-7 gap-2">
        {days.map(day => {
          const dayAppointments = getAppointmentsForDate(day)
          const isDayToday = isToday(day)

          return (
            <div key={day.toISOString()} className="flex flex-col">
              <div
                className={cn(
                  'text-center py-2 mb-2 rounded-lg',
                  isDayToday && 'bg-primary text-primary-foreground'
                )}
              >
                <div className="text-xs font-medium">{format(day, 'EEE')}</div>
                <div className="text-lg font-bold">{format(day, 'd')}</div>
              </div>

              <div className="flex-1 border rounded-lg p-2 space-y-2 min-h-[400px] bg-card">
                {dayAppointments.length === 0 ? (
                  <div className="text-xs text-muted-foreground text-center py-4">
                    No appointments
                  </div>
                ) : (
                  dayAppointments.map(apt => (
                    <div
                      key={apt.id}
                      onClick={() => router.visit(`/admin/appointments/${apt.id}`)}
                      className={cn(
                        'p-2 rounded cursor-pointer hover:opacity-80 transition-opacity',
                        getTypeBadgeColor(apt.appointment_type)
                      )}
                    >
                      <div className="flex items-center gap-1 mb-1">
                        <ClockIcon className="h-3 w-3" />
                        <span className="text-xs font-semibold">{apt.formatted_time}</span>
                      </div>
                      <div className="text-sm font-medium truncate">{apt.patient.display_name}</div>
                      <div className="text-xs truncate">{apt.type_label}</div>
                      <div className="text-xs opacity-70">{apt.duration_minutes} min</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  const renderDayView = () => {
    const dayAppointments = getAppointmentsForDate(currentDate)
    const hours = Array.from({ length: 24 }, (_, i) => i)

    return (
      <div className="border rounded-lg bg-card">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold">{format(currentDate, 'EEEE, MMMM d, yyyy')}</h3>
          <p className="text-sm text-muted-foreground">
            {dayAppointments.length} appointment{dayAppointments.length !== 1 ? 's' : ''}
          </p>
        </div>

        <div className="p-4 space-y-3 max-h-[600px] overflow-y-auto">
          {dayAppointments.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <CalendarIcon className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No appointments scheduled for this day</p>
            </div>
          ) : (
            dayAppointments.map(apt => (
              <Card
                key={apt.id}
                onClick={() => router.visit(`/admin/appointments/${apt.id}`)}
                className="cursor-pointer hover:shadow-md transition-shadow"
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={getTypeBadgeColor(apt.appointment_type)}>
                          {apt.type_label}
                        </Badge>
                        <Badge variant="outline">{apt.status_label}</Badge>
                      </div>

                      <div className="flex items-center gap-2 mb-1">
                        <UserIcon className="h-4 w-4 text-muted-foreground" />
                        <span className="font-semibold">{apt.patient.display_name}</span>
                      </div>

                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <ClockIcon className="h-4 w-4" />
                        <span>{apt.formatted_time}</span>
                        <span>•</span>
                        <span>{apt.duration_minutes} minutes</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    )
  }

  const getViewTitle = () => {
    if (view === 'month') {
      return format(currentDate, 'MMMM yyyy')
    } else if (view === 'week') {
      const weekStart = startOfWeek(currentDate)
      const weekEnd = endOfWeek(currentDate)
      return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`
    } else {
      return format(currentDate, 'EEEE, MMMM d, yyyy')
    }
  }

  return (
    <div className="space-y-4">
      {/* Calendar controls */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleToday}>
            Today
          </Button>
          <Button variant="ghost" size="icon" onClick={handlePrevious}>
            <ChevronLeftIcon className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={handleNext}>
            <ChevronRightIcon className="h-4 w-4" />
          </Button>
          <h2 className="text-lg font-semibold">{getViewTitle()}</h2>
        </div>

        <ToggleGroup type="single" value={view} onValueChange={(value) => value && setView(value as CalendarView)}>
          <ToggleGroupItem value="month" aria-label="Month view">
            Month
          </ToggleGroupItem>
          <ToggleGroupItem value="week" aria-label="Week view">
            Week
          </ToggleGroupItem>
          <ToggleGroupItem value="day" aria-label="Day view">
            Day
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      {/* Calendar view */}
      <div>
        {view === 'month' && renderMonthView()}
        {view === 'week' && renderWeekView()}
        {view === 'day' && renderDayView()}
      </div>
    </div>
  )
}
